import React, { useState, useEffect, useCallback } from 'react';
import { materialesAsignadosAPI } from '../api/api';

// Fecha AAAA-MM-DD de hace `dias` días (filtro por defecto: lo reciente)
const haceDias = (dias) => {
  const d = new Date();
  d.setDate(d.getDate() - dias);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
};

const MaterialAsignado = () => {
  const [asignaciones, setAsignaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState('');
  // U-89 (lote 5b): sin tope de 500; por defecto, eventos desde hace 30 días
  const [filtroDesde, setFiltroDesde] = useState(haceDias(30));

  const estadosMap = {
    'reservado': { label: 'Reservado', color: 'bg-yellow-100 text-yellow-800' },
    'entregado': { label: 'Entregado', color: 'bg-blue-100 text-blue-800' },
    'devuelto': { label: 'Devuelto', color: 'bg-green-100 text-green-800' }
  };

  const fetchAsignaciones = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filtroEstado) params.estado = filtroEstado;
      if (filtroDesde) params.desde = filtroDesde;
      const response = await materialesAsignadosAPI.getAll(params);
      setAsignaciones(response.data);
    } catch (error) {
      console.error('Error fetching asignaciones:', error);
    } finally {
      setLoading(false);
    }
  }, [filtroEstado, filtroDesde]);

  useEffect(() => {
    fetchAsignaciones();
  }, [fetchAsignaciones]);

  const handleCambiarEstado = async (asignacionId, nuevoEstado) => {
    try {
      await materialesAsignadosAPI.cambiarEstado(asignacionId, nuevoEstado);
      fetchAsignaciones();
    } catch (error) {
      console.error('Error cambiando estado:', error);
      alert('Error al cambiar el estado');
    }
  };

  const handleDeleteAsignacion = async (asignacionId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta asignación?')) return;
    
    try {
      await materialesAsignadosAPI.delete(asignacionId);
      fetchAsignaciones();
    } catch (error) {
      console.error('Error deleting asignacion:', error);
      alert('Error al eliminar la asignación');
    }
  };

  const formatFecha = (fecha) => {
    if (!fecha) return 'N/A';
    return new Date(fecha).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFechaSolo = (fecha) => {
    if (!fecha) return 'N/A';
    return new Date(fecha).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const AsignacionCard = ({ asignacion }) => {
    const [expanded, setExpanded] = useState(false);

    return (
      <div className="bg-white rounded-lg shadow-md p-6 mb-4 hover:shadow-lg transition-shadow">
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-3">
              <h3 className="text-lg font-semibold text-gray-800">{asignacion.material_nombre}</h3>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${estadosMap[asignacion.estado]?.color || 'bg-gray-100'}`}>
                {estadosMap[asignacion.estado]?.label || asignacion.estado}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm text-gray-600 mb-3">
              <div>
                <span className="font-medium">Cantidad:</span> {asignacion.cantidad_asignada} {asignacion.unidad}
              </div>
              <div>
                <span className="font-medium">Fecha Evento:</span> {formatFechaSolo(asignacion.fecha_evento)}
              </div>
              {asignacion.ubicacion_destino && (
                <div className="col-span-2">
                  <span className="font-medium">Destino:</span> {asignacion.ubicacion_destino}
                </div>
              )}
            </div>

            {expanded && (
              <div className="mt-4 pt-4 border-t border-gray-200 space-y-2 text-sm text-gray-600">
                {asignacion.fecha_necesaria_desde && (
                  <div>
                    <span className="font-medium">Necesario desde:</span> {formatFecha(asignacion.fecha_necesaria_desde)}
                  </div>
                )}
                {asignacion.fecha_necesaria_hasta && (
                  <div>
                    <span className="font-medium">Necesario hasta:</span> {formatFecha(asignacion.fecha_necesaria_hasta)}
                  </div>
                )}
                {asignacion.notas && (
                  <div>
                    <span className="font-medium">Notas:</span> {asignacion.notas}
                  </div>
                )}
                <div>
                  <span className="font-medium">ID Presupuesto:</span> {asignacion.presupuesto_id}
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2 ml-4">
            {asignacion.estado === 'reservado' && (
              <button
                onClick={() => handleCambiarEstado(asignacion.id, 'entregado')}
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition whitespace-nowrap"
              >
                → Entregar
              </button>
            )}
            {asignacion.estado === 'entregado' && (
              <button
                onClick={() => handleCambiarEstado(asignacion.id, 'devuelto')}
                className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition whitespace-nowrap"
              >
                ← Devolver
              </button>
            )}
            <button
              onClick={() => setExpanded(!expanded)}
              className="px-4 py-2 bg-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-300 transition"
            >
              {expanded ? '▲ Menos' : '▼ Más'}
            </button>
            <button
              onClick={() => handleDeleteAsignacion(asignacion.id)}
              className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition"
            >
              Eliminar
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-xl text-gray-600">Cargando asignaciones...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Material Asignado</h1>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Eventos desde</label>
            <input
              type="date"
              value={filtroDesde}
              onChange={(e) => setFiltroDesde(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Estado</label>
            <select
              value={filtroEstado}
              onChange={(e) => setFiltroEstado(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos</option>
              <option value="reservado">Reservado</option>
              <option value="entregado">Entregado</option>
              <option value="devuelto">Devuelto</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => { setFiltroEstado(''); setFiltroDesde(''); }}
              className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
            >
              Ver todo
            </button>
          </div>
        </div>
      </div>

      {/* Estadísticas rápidas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
          <div className="text-2xl font-bold text-yellow-800">
            {asignaciones.filter(a => a.estado === 'reservado').length}
          </div>
          <div className="text-sm text-yellow-700">Reservados</div>
        </div>
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
          <div className="text-2xl font-bold text-blue-800">
            {asignaciones.filter(a => a.estado === 'entregado').length}
          </div>
          <div className="text-sm text-blue-700">Entregados</div>
        </div>
        <div className="bg-green-50 rounded-lg p-4 border border-green-200">
          <div className="text-2xl font-bold text-green-800">
            {asignaciones.filter(a => a.estado === 'devuelto').length}
          </div>
          <div className="text-sm text-green-700">Devueltos</div>
        </div>
      </div>

      {/* Lista de asignaciones */}
      {asignaciones.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <div className="text-gray-400 text-xl mb-2">No hay materiales asignados</div>
          <p className="text-gray-500">Los materiales se asignan automáticamente al confirmar presupuestos</p>
        </div>
      ) : (
        <div>
          {asignaciones.map(asignacion => (
            <AsignacionCard key={asignacion.id} asignacion={asignacion} />
          ))}
        </div>
      )}
    </div>
  );
};

export default MaterialAsignado;
